import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import batchService from '../../../../services/mentor/batchService';
import type { IBatch } from '../../../../types/batch';
import type { User } from '../../../../types/user';
import type { IPopulatedEnrollment } from '../../../../types/enrollment';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { ArrowLeft, Users, User as UserIcon } from 'lucide-react';

const BatchDetailPage: React.FC = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const [batch, setBatch] = useState<IBatch | null>(null);
    const [students, setStudents] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchBatchData = async () => {
            if (!batchId) return;

            setIsLoading(true);
            try {
                const [batchData, enrollmentsData] = await Promise.all([
                    batchService.getBatchById(batchId),
                    batchService.getBatchStudents(batchId)
                ]);
                
                setBatch(batchData);
                
                const studentList = enrollmentsData.map((enrollment: IPopulatedEnrollment) => enrollment.student).filter(Boolean);
                setStudents(studentList);

            } catch (err: any) {
                showToast(err.message, { type: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        fetchBatchData();
    }, [batchId]);

    if (isLoading) {
        return <Loader />;
    }

    if (!batch) {
        return <p className="text-center text-red-500">Batch not found.</p>;
    }

    // A helper to safely get the course ID
    const courseId = typeof batch.course === 'string' ? batch.course : batch.course._id;

    return (
        <div className="space-y-6">
            {/* ✅ FIX: Use the safe courseId variable */}
            <Link to={`/dashboard/mentor/courses/${courseId}`} className="inline-flex items-center gap-2 text-blue-600 hover:underline font-medium">
                <ArrowLeft size={16} /> Back to Course Details
            </Link>

            <header className="bg-white p-6 rounded-2xl shadow-sm border">
                <h1 className="text-3xl font-bold text-gray-800">
                    {batch.name}
                </h1>
                <p className="text-gray-500 mt-1">
                    Viewing all students enrolled in this batch.
                </p>
            </header>

            <div className="bg-white p-6 rounded-2xl shadow-sm border">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Enrolled Students ({students.length})</h2>
                {students.length === 0 ? (
                    <div className="text-center py-12">
                         <Users className="mx-auto h-12 w-12 text-gray-400" />
                         <h3 className="mt-2 text-lg font-medium text-gray-900">No Students Enrolled</h3>
                         <p className="mt-1 text-sm text-gray-500">There are no students enrolled in this batch yet.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {students.map(student => (
                            <div key={student._id} className="p-4 border rounded-lg flex items-center gap-4 bg-gray-50">
                               <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                                   <UserIcon size={20} />
                               </div>
                               <div>
                                   <p className="font-medium text-gray-900 truncate">{student.name}</p>
                                   <p className="text-sm text-gray-500 truncate">{student.email}</p>
                               </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BatchDetailPage;